import { useRef } from 'react';
import cn from 'classnames';
// Ui
import Text from 'ui3/Text/Text';
import Switch from 'ui3/Switch/Switch';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
// Components
import CompetitorSlot from '../CompetitorSlot/CompetitorSlot';
import { BoutDraft, CompetitorDraft } from '../types';
// Styles
import styles from './BoutCard.module.scss';

type BoutCardProps = {
  bout: BoutDraft;
  index: number;
  onChange: (bout: BoutDraft) => void;
  onRemove: () => void;
  excludeStoreIds?: string[];
  isDragging?: boolean;
  isDropTarget?: boolean;
  onDragStart?: (index: number) => void;
  onDragOver?: (index: number) => void;
  onDragEnd?: () => void;
  onDrop?: (index: number) => void;
  onReorder?: (fromIndex: number, toIndex: number) => void;
};

const BoutCard = ({
  bout,
  index,
  onChange,
  onRemove,
  excludeStoreIds,
  isDragging = false,
  isDropTarget = false,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop,
  onReorder,
}: BoutCardProps): JSX.Element => {
  const rootRef = useRef<HTMLDivElement | null>(null);

  const handleMainEventChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...bout, isMainEvent: e.target.checked });
  };

  const handleCompetitorAChange = (competitorA: CompetitorDraft) => {
    onChange({ ...bout, competitorA, competitorASearch: undefined });
  };

  const handleCompetitorBChange = (competitorB: CompetitorDraft) => {
    onChange({ ...bout, competitorB, competitorBSearch: undefined });
  };

  const handleCompetitorASearchChange = (competitorASearch: string) => {
    onChange({ ...bout, competitorASearch });
  };

  const handleCompetitorBSearchChange = (competitorBSearch: string) => {
    onChange({ ...bout, competitorBSearch });
  };

  return (
    <div
      ref={rootRef}
      className={cn(styles.root, {
        [styles.mainEvent]: bout.isMainEvent,
        [styles.dragging]: isDragging,
        [styles.dropTarget]: isDropTarget,
      })}
      onDragOver={(event) => {
        event.preventDefault();
        onDragOver?.(index);
      }}
      onDrop={(event) => {
        event.preventDefault();
        onDrop?.(index);
      }}
    >
      <div
        className={styles.header}
        draggable
        onDragStart={(event) => {
          event.dataTransfer.effectAllowed = 'move';
          event.dataTransfer.setData('text/plain', String(index));
          if (
            rootRef.current &&
            typeof event.dataTransfer.setDragImage === 'function'
          ) {
            const { left, top } = rootRef.current.getBoundingClientRect();
            event.dataTransfer.setDragImage(
              rootRef.current,
              event.clientX - left,
              event.clientY - top
            );
          }

          onDragStart?.(index);
        }}
        onDragEnd={() => onDragEnd?.()}
      >
        <div className={styles.headerLeft}>
          <button
            type="button"
            className={styles.dragHandleWrapper}
            aria-label={`Drag bout ${index + 1}`}
            title="Move with arrow keys"
            onKeyDown={(event) => {
              if (event.key === 'ArrowUp') {
                event.preventDefault();
                onReorder?.(index, index - 1);
              }
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                onReorder?.(index, index + 1);
              }
            }}
          >
            <img
              src="/images/create-stream-wizard/bout-drag-handle.svg"
              alt=""
              className={styles.dragHandle}
              draggable={false}
            />
          </button>
          <Text variant="body2Medium" color="lights-low">
            {`BOUT ${index + 1}`}
          </Text>
          {bout.isMainEvent && (
            <Text variant="captionRegular" className={styles.mainEventBadge}>
              Main Event
            </Text>
          )}
        </div>

        <div className={styles.headerActions}>
          <Text variant="body2Medium">Main Event</Text>
          <Switch
            name={`${bout.id}-isMainEvent`}
            checked={bout.isMainEvent}
            onChange={handleMainEventChange}
          />
          <button
            type="button"
            className={styles.deleteButton}
            aria-label="Delete bout"
            onClick={onRemove}
          >
            <MillionsIcon name="trash" size={20} />
          </button>
        </div>
      </div>

      <div className={styles.details}>
        <div className={styles.competitors}>
          <CompetitorSlot
            corner="A"
            competitor={bout.competitorA}
            onChange={handleCompetitorAChange}
            excludeStoreIds={excludeStoreIds}
            searchTerm={bout.competitorASearch}
            onSearchTermChange={handleCompetitorASearchChange}
          />
          <div className={styles.vs}>
            <img
              src="/images/create-stream-wizard/swords.svg"
              alt=""
              className={styles.vsIcon}
            />
            <Text variant="buttonL" color="lights-low">
              VS
            </Text>
          </div>
          <CompetitorSlot
            corner="B"
            competitor={bout.competitorB}
            onChange={handleCompetitorBChange}
            excludeStoreIds={excludeStoreIds}
            searchTerm={bout.competitorBSearch}
            onSearchTermChange={handleCompetitorBSearchChange}
          />
        </div>
      </div>
    </div>
  );
};

export default BoutCard;
